// Square Code V2 Agent Command — presentational shell (Agent Runtime
// Reconciliation, restyle 2026-07-14).
//
// Pure presentational component: every prop is owned and fetched by the
// route wrapper (SquareCodeAgentsOverviewRoute.tsx), same as the
// OperationsAgentCommandCenter / DailyMeetingsWorkroom split elsewhere in
// this tree. No fetch, no navigation state, no fabricated data lives here —
// `agents` is exactly what api.commandCenter.agents.list() returned.
//
// Visual language: the approved "neon void" design reference
// (SQ_TeamControl.html / SQ_RecursiveMatrix.html) — near-black background,
// upper-right radial atmosphere, fine grid overlay, Chakra Petch display
// type, JetBrains Mono labels, small status-chip pills, and an honest
// disclosure footer. Content (team names, chat, role model) from those
// references was NOT reused — only the visual/interaction language, applied
// to this project's own real data and its own required status-accent
// mapping (see ./theme.ts).

import type { CSSProperties } from 'react'
import type { RegistryAgent } from '../../../api/client'
import AgentStatusChip from './AgentStatusChip'
import AgentCommandStage, { type AgentCommandStageProps } from './AgentCommandStage'
import AgentApprovalQueue, { type AgentApprovalQueueProps } from './AgentApprovalQueue'
import AgentRecursiveMatrix from './AgentRecursiveMatrix'
import SurfaceDirectory from './SurfaceDirectory'
import {
  AGENT_FONT,
  AGENT_IDENTITY_ACCENT,
  AGENT_STATUS_ACCENT,
  AGENT_VOID,
  AGENT_VOID_BACKGROUND,
  agentStatusAccent,
  gateAccent,
} from './theme'

export type AgentRosterState = 'loading' | 'ready' | 'auth_required' | 'forbidden' | 'error'

export interface AgentCommandCenterProps {
  state: AgentRosterState
  agents: RegistryAgent[]
  errorMessage: string | null
  onNavigate: (path: string) => void
  onBack: () => void

  // Command stage (commit_2) — a typed front end for
  // api.agentWorkspace.actionRequests.create(), owned by the route
  // wrapper's live hooks.
  stage: Omit<AgentCommandStageProps, 'agents'>

  // Approval queue (commit_2) — a real read+decide view of
  // sq_agent_action_requests, owned by the route wrapper's live hooks.
  queue: Omit<AgentApprovalQueueProps, 'agents'>
}

function initialsFor(agent: RegistryAgent): string {
  const name = (agent.display_name ?? '').trim()
  if (name) {
    const parts = name.split(/\s+/).filter(Boolean)
    if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase()
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
  }
  return agent.agent_id.slice(0, 2).toUpperCase()
}

function primarySkillLabel(agent: RegistryAgent): string | null {
  const skills = agent.skills ?? []
  if (skills.length === 0) return null
  const primary = skills.find((s) => s.priority === 'primary') ?? skills[0]
  return primary.skill_id
}

function rosterChip(state: AgentRosterState, count: number, errorMessage: string | null) {
  switch (state) {
    case 'loading':
      return { label: 'SYNCING ROSTER…', accent: AGENT_STATUS_ACCENT.cyan, title: 'Reading sq_agent_registry live via api.commandCenter.agents.list().' }
    case 'ready':
      return count > 0
        ? { label: `${count} AGENT${count === 1 ? '' : 'S'} LIVE`, accent: AGENT_STATUS_ACCENT.green, title: 'Live count from sq_agent_registry — never a fixed number.' }
        : { label: '0 AGENTS REGISTERED', accent: AGENT_STATUS_ACCENT.amber, title: 'sq_agent_registry has no rows yet — agents are seeded by migrations.' }
    case 'auth_required':
      return { label: 'SIGN-IN REQUIRED', accent: AGENT_STATUS_ACCENT.amber, title: 'Sign in to read the live roster.' }
    case 'forbidden':
      return { label: 'ROSTER ACCESS DENIED', accent: AGENT_STATUS_ACCENT.red, title: 'Your role does not have access to the agent roster.' }
    case 'error':
    default:
      return { label: 'ROSTER LOAD FAILED', accent: AGENT_STATUS_ACCENT.red, title: errorMessage ?? undefined }
  }
}

const sectionLabelStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  fontFamily: AGENT_FONT.mono,
  fontSize: 9,
  fontWeight: 700,
  letterSpacing: '0.14em',
  color: AGENT_VOID.faint,
  textTransform: 'uppercase',
  margin: '0 0 11px',
}

function SectionLabel({ text, trailing }: { text: string; trailing?: string }) {
  return (
    <div style={sectionLabelStyle}>
      <span>{text}</span>
      {trailing && <span style={{ color: AGENT_VOID.hairline }}>· {trailing}</span>}
      <span style={{ flex: 1, height: 1, background: `linear-gradient(90deg, ${AGENT_IDENTITY_ACCENT}30, transparent)` }} />
    </div>
  )
}

export default function AgentCommandCenter({ state, agents, errorMessage, onNavigate, onBack, stage, queue }: AgentCommandCenterProps) {
  const chip = rosterChip(state, agents.length, errorMessage)

  return (
    <div
      style={{
        ...AGENT_VOID_BACKGROUND,
        minHeight: '100vh',
        color: AGENT_VOID.ink,
        fontFamily: AGENT_FONT.body,
        WebkitFontSmoothing: 'antialiased' as CSSProperties['WebkitFontSmoothing'],
        padding: '24px 22px 56px',
      }}
    >
      <style>{`
        /* Breakpoints match the approved design-reference conventions
           (SQ_TeamControl.html): .grid2 -> 1fr under 920px,
           .surf-grid -> 1fr under 620px. The command-stage's own
           2-column field grid follows the same 620px convention as the
           other 2-column card grid on this page (surf-grid). */
        .sqv2-agents-grid { display: grid; grid-template-columns: 300px 1fr; gap: 16px; }
        @media (max-width: 920px) { .sqv2-agents-grid { grid-template-columns: 1fr; } }
        .sqv2-agents-surfgrid { display: grid; grid-template-columns: 1fr 1fr; gap: 11px; }
        @media (max-width: 620px) { .sqv2-agents-surfgrid { grid-template-columns: 1fr; } }
        .sqv2-agents-stagegrid { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
        @media (max-width: 620px) { .sqv2-agents-stagegrid { grid-template-columns: 1fr; } }
        .sqv2-agents-surf { transition: border-color 150ms ease, box-shadow 150ms ease; }
        .sqv2-agents-surf:hover { border-color: rgba(56,182,255,0.5); box-shadow: 0 0 18px rgba(56,182,255,0.14); }
        /* Motion-sensitive users: no hover transition, snap directly to
           the hover state instead of animating toward it. */
        @media (prefers-reduced-motion: reduce) {
          .sqv2-agents-surf { transition: none; }
        }
      `}</style>

      <div style={{ maxWidth: 1160, margin: '0 auto' }}>
        {/* Honest state-summary chip row */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 7, marginBottom: 22 }} aria-label="Agent system status">
          <AgentStatusChip label={chip.label} accent={chip.accent} title={chip.title} />
          <AgentStatusChip
            label="LINKS · NOT FAKED"
            accent={AGENT_STATUS_ACCENT.green}
            title="The five surfaces below are the actual registered routes — not mockups."
          />
          <AgentStatusChip
            label="ENGINE V4 · FUNCTIONS DEFERRED"
            accent={AGENT_STATUS_ACCENT.amber}
            title="All 8 Engine V4 tables exist live. Its 6 execution functions are not deployed — deferred by operator choice."
          />
        </div>

        {/* Breadcrumb + back */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 2 }}>
          <button
            type="button"
            onClick={onBack}
            style={{
              background: 'none',
              border: 'none',
              color: AGENT_VOID.muted,
              cursor: 'pointer',
              fontSize: 12.5,
              padding: 0,
              fontFamily: AGENT_FONT.body,
            }}
          >
            ← Command Center
          </button>
          <span style={{ fontFamily: AGENT_FONT.mono, fontSize: 9.5, color: AGENT_VOID.faint }}>
            COMMAND CENTER › AGENTS
          </span>
        </div>

        <div
          style={{
            fontFamily: AGENT_FONT.mono,
            fontSize: 9,
            fontWeight: 700,
            letterSpacing: '0.24em',
            color: AGENT_IDENTITY_ACCENT,
            textTransform: 'uppercase',
            margin: '14px 0 4px',
          }}
        >
          Agent Command
        </div>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: 20, flexWrap: 'wrap', marginBottom: 22 }}>
          <h1
            style={{
              fontFamily: AGENT_FONT.display,
              fontWeight: 700,
              fontSize: 38,
              letterSpacing: '0.01em',
              textTransform: 'uppercase',
              lineHeight: 0.98,
              margin: 0,
              textShadow: `0 0 34px ${AGENT_IDENTITY_ACCENT}52`,
            }}
          >
            Agent Operations
          </h1>
          <p style={{ fontSize: 12.5, color: AGENT_VOID.muted, maxWidth: 520, lineHeight: 1.6, margin: 0 }}>
            One entry point to the governed agent system. Every action below happens on its owning
            surface — this page only reads the real roster and links out.
          </p>
        </div>

        <div className="sqv2-agents-grid">
          {/* Roster */}
          <section aria-labelledby="agent-roster-heading">
            <h2 id="agent-roster-heading" style={{ ...sectionLabelStyle, margin: '0 0 11px' }}>
              <span>Registered agents</span>
              <span style={{ color: AGENT_VOID.hairline }}>· {state === 'ready' ? agents.length : '—'}</span>
              <span style={{ flex: 1, height: 1, background: `linear-gradient(90deg, ${AGENT_IDENTITY_ACCENT}30, transparent)` }} />
            </h2>

            {/* Live region: announces the loading -> ready/auth_required/
                forbidden/error transition to assistive tech. This one
                wrapper stays mounted across every state change so the
                transition itself (not just the final content) is heard. */}
            <div aria-live="polite" aria-atomic="false">
              {state === 'loading' && <p style={{ color: AGENT_VOID.muted, fontSize: 13 }}>Loading roster…</p>}
              {state === 'auth_required' && <p style={{ color: AGENT_VOID.muted, fontSize: 13 }}>Sign in to view the agent roster.</p>}
              {state === 'forbidden' && <p style={{ color: AGENT_VOID.muted, fontSize: 13 }}>Your role does not have access to the agent roster.</p>}
              {state === 'error' && (
                <p style={{ color: AGENT_STATUS_ACCENT.red, fontSize: 13 }}>Could not load the roster: {errorMessage}</p>
              )}
              {state === 'ready' && agents.length === 0 && (
                <p style={{ color: AGENT_VOID.muted, fontSize: 13 }}>No agents are registered in sq_agent_registry yet.</p>
              )}

              {state === 'ready' && agents.length > 0 && (
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  {agents.map((agent) => {
                  const accent = agentStatusAccent(agent.status)
                  const skill = primarySkillLabel(agent)
                  const gAccent = gateAccent(agent.default_gate)
                  return (
                    <div
                      key={agent.agent_id}
                      style={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: 11,
                        padding: '10px 4px',
                        borderBottom: `1px solid ${AGENT_VOID.hairline}`,
                      }}
                    >
                      <span
                        style={{
                          width: 34,
                          height: 34,
                          borderRadius: 9,
                          display: 'grid',
                          placeItems: 'center',
                          fontFamily: AGENT_FONT.mono,
                          fontWeight: 800,
                          fontSize: 10,
                          flexShrink: 0,
                          position: 'relative',
                          border: `1px solid ${accent}66`,
                          color: accent,
                          background: `${accent}14`,
                        }}
                      >
                        {initialsFor(agent)}
                        {/* Decorative status dot — the accessible status
                            text already lives in the badge next to
                            display_name below; aria-hidden avoids an
                            unlabeled duplicate stop for screen readers. */}
                        <span
                          aria-hidden="true"
                          title={agent.status ?? 'unknown'}
                          style={{
                            position: 'absolute',
                            bottom: -2,
                            right: -2,
                            width: 9,
                            height: 9,
                            borderRadius: '50%',
                            background: accent,
                            border: `2px solid ${AGENT_VOID.bg}`,
                          }}
                        />
                      </span>
                      <div style={{ minWidth: 0, flex: 1 }}>
                        <div
                          style={{
                            fontFamily: AGENT_FONT.display,
                            fontWeight: 600,
                            fontSize: 13,
                            color: AGENT_VOID.ink,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 7,
                            flexWrap: 'wrap',
                          }}
                        >
                          {agent.display_name}
                          <span
                            style={{
                              fontFamily: AGENT_FONT.mono,
                              fontSize: 7.5,
                              fontWeight: 800,
                              padding: '1px 5px',
                              borderRadius: 4,
                              border: `1px solid ${accent}55`,
                              color: accent,
                              background: `${accent}12`,
                              textTransform: 'uppercase',
                            }}
                          >
                            {agent.status ?? 'unknown'}
                          </span>
                        </div>
                        <div style={{ fontFamily: AGENT_FONT.mono, fontSize: 8.5, color: AGENT_VOID.faint, marginTop: 2 }}>
                          {agent.agent_id} · {agent.lane ?? 'unlabeled lane'}
                        </div>
                        <div style={{ display: 'flex', gap: 6, marginTop: 6, flexWrap: 'wrap' }}>
                          <span
                            style={{
                              fontFamily: AGENT_FONT.mono,
                              fontSize: 7.5,
                              fontWeight: 700,
                              padding: '1px 6px',
                              borderRadius: 4,
                              border: `1px solid ${gAccent}55`,
                              color: gAccent,
                              background: `${gAccent}10`,
                            }}
                          >
                            gate · {agent.default_gate ?? 'unset'}
                          </span>
                          {skill && (
                            <span
                              style={{
                                fontFamily: AGENT_FONT.mono,
                                fontSize: 7.5,
                                fontWeight: 700,
                                padding: '1px 6px',
                                borderRadius: 4,
                                border: `1px solid ${AGENT_VOID.hairline}`,
                                color: AGENT_VOID.faint,
                              }}
                            >
                              skill · {skill}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  )
                })}
              </div>
            )}
            </div>
          </section>

          {/* Surface directory (commit_3) + honesty footer */}
          <section aria-labelledby="agent-surfaces-heading">
            <h2 id="agent-surfaces-heading" style={{ margin: 0 }}>
              <SectionLabel text="Surface directory" trailing="5 canonical, already-shipped destinations" />
            </h2>

            <SurfaceDirectory onNavigate={onNavigate} />

            <div
              style={{
                fontFamily: AGENT_FONT.mono,
                fontSize: 8.5,
                color: AGENT_VOID.faint,
                lineHeight: 1.7,
                padding: '13px 15px',
                borderRadius: 12,
                background: 'rgba(14,10,22,0.4)',
                border: `1px solid ${AGENT_VOID.hairline}`,
                marginTop: 16,
              }}
            >
              <b style={{ color: AGENT_VOID.muted }}>What&rsquo;s real.</b> This page reads sq_agent_registry live via
              the same api.commandCenter.agents.list() endpoint Agent Control Center uses — the count and every
              status/gate above reflect the live table, never a fixed number. The five surfaces above are the actual
              registered routes, not mockups: Agent Control Center, Agent Workspace, and Activation Doctor are fully
              open; Operations Agent Command runs the real command dispatcher against a live approval-queue read; and
              Engine Runtime V4&rsquo;s 8 tables exist live while its 6 execution functions remain undeployed —
              deferred by operator choice, not broken. Pause/activate control still stays on Agent Control Center. The
              command stage below prepares a real sq_agent_action_requests row (no second dispatcher, no fabricated
              result); the approval queue below reads and decides those same real rows via the exact endpoints Agent
              Workspace already uses — self-approval is denied server-side, not just hidden in this UI. The recursive
              matrix below restates these same facts as five explicit dimensions — registry and approval-queue counts
              live, Post Jobs and the Higgsfield provider slot from the real code paths, Engine Runtime V4 exactly as
              stated above — never a fabricated analysis.
            </div>
          </section>
        </div>

        {/* Recursive matrix (commit_3) — five real dimensions, each with
            an honest lead badge, result readout, and status tag. Restyled
            from the SQ_RecursiveMatrix.html reference's dimension-row
            pattern; the dimensions themselves are this project's own real
            surfaces, never the prototype's fictional ones. */}
        <section aria-labelledby="agent-recursive-matrix-heading" style={{ marginTop: 26 }}>
          <h2 id="agent-recursive-matrix-heading" style={{ margin: 0 }}>
            <SectionLabel text="Recursive matrix" trailing="honest readiness across five real dimensions" />
          </h2>
          <div style={{ borderRadius: 12, border: `1px solid ${AGENT_VOID.hairline}`, background: 'rgba(14,10,22,0.4)', padding: '16px' }}>
            <AgentRecursiveMatrix
              rosterState={state}
              agents={agents}
              queueWorkspaceState={queue.workspaceState}
              queueState={queue.queueState}
              queueRows={queue.rows}
            />
          </div>
        </section>

        {/* Command stage (commit_2) — a typed front end for the real
            Agent Workspace action-request endpoint. api.commands.* has
            no agent-directed verb, so this intentionally does not touch
            that dispatcher. */}
        <section aria-labelledby="agent-command-stage-heading" style={{ marginTop: 26 }}>
          <h2 id="agent-command-stage-heading" style={{ margin: 0 }}>
            <SectionLabel text="Command stage" trailing="prepares a real agent action request" />
          </h2>
          <div style={{ borderRadius: 12, border: `1px solid ${AGENT_VOID.hairline}`, background: 'rgba(14,10,22,0.4)', padding: '16px 16px 18px' }}>
            <AgentCommandStage agents={agents} {...stage} />
          </div>
        </section>

        {/* Approval queue (commit_2) — real read+decide over
            sq_agent_action_requests, same endpoints Operations Agent
            Command's read-only queue and the Agent Workspace surface
            already use. */}
        <section aria-labelledby="agent-approval-queue-heading" style={{ marginTop: 22 }}>
          <h2 id="agent-approval-queue-heading" style={{ margin: 0 }}>
            <SectionLabel text="Approval queue" trailing="live sq_agent_action_requests" />
          </h2>
          <div style={{ borderRadius: 12, border: `1px solid ${AGENT_VOID.hairline}`, background: 'rgba(14,10,22,0.4)', padding: '16px' }}>
            <AgentApprovalQueue agents={agents} {...queue} />
          </div>
        </section>
      </div>
    </div>
  )
}
